import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { LucideAngularModule } from 'lucide-angular';
import { Icons } from '../../../../shared/helpers/icons';
import { IContact } from '../../../../shared/models/contact';
import { IAfterSchoolActivity } from '../../../../shared/models/afterschoolActivity';
import { UserDataService } from '../../../../shared/services/userData.service';
import { ContactsService } from '../../../../shared/services/contact.service';
import { AfterschoolActivityService } from '../../../../shared/services/afterschoolActivity.service';
import { SpinnerComponent } from '../../../components/spinner/spinner.component';

@Component({
  selector: 'edit-activity',
  standalone: true,
  imports: [
    CommonModule,
    LucideAngularModule, 
    FormsModule,
    SpinnerComponent
  ],
  templateUrl: './edit-activity.component.html',
  styleUrl: './edit-activity.component.css'
})

export class EditActivityComponent implements OnInit {
  icons = Icons;
  currentBuildingId: string | null = null;
  fullContactsList: IContact[] = [];
  contactsList: IContact[] = [];

  @Input() activityToEdit: IAfterSchoolActivity | null = null;
  @Input() showFullDetails: boolean = true;

  @Output() onClose = new EventEmitter<void>();
  @Output() onSave = new EventEmitter<any>();

  activityData: IAfterSchoolActivity = {
    activity_name: '',
    location: '',
    primary_contact_id: 0,
    secondary_contact_id: 0,
    emergency_equipment: '',
    first_aid_contact_ids: [],
    cpr_contact_ids: [],
    aed_contact_ids: [],
    incident_reporting_contact_ids: [],
    ieap_contact_ids: [],
    emergency_diabetic_contact_ids: [],
    courses_of_action: ''
  };
  
  suggestions: string[] = [ 'First', 'Aid', 'Cell', 'AED', 'Landline', 'Two-Way Radio', 'Emergency Transportation', 'Weather Radio', 'Blood Borne Pathogens Kit' ];
  selectedContacts = {
    first_aid: [] as { id: number; name: string; email: string }[],
    cpr: [] as { id: number; name: string; email: string }[],
    aed: [] as { id: number; name: string; email: string }[],
    incident_reporting: [] as { id: number; name: string; email: string }[],
    ieap: [] as { id: number; name: string; email: string }[],
    emergency_diabetic: [] as { id: number; name: string; email: string }[]
  };

  loading: boolean = false;
  showSuggestions: boolean = false;

  constructor(
    private userDataService: UserDataService,
    private contactsService: ContactsService,
    private afterschoolActivityService: AfterschoolActivityService
  ) {}

  ngOnInit(): void {
    this.currentBuildingId = this.userDataService.getBuildingId();
    this.loadContacts();
  }

  // ✅ react to changes in the input
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['activityToEdit'] && this.activityToEdit) {
      this.populateActivityData();
    }
  }

  loadContacts(): void {
    this.loading = true;
    this.contactsService.getContacts(this.currentBuildingId!).subscribe({
      next: (response) => {
        this.loading = false;
        this.fullContactsList = response.data || [];
        this.contactsList = [...this.fullContactsList].sort((a, b) => a.name.localeCompare(b.name));

        // if no contact set in material, default to first
        if (this.contactsList.length > 0) {
          this.contactsList.sort((a, b) => a.name.localeCompare(b.name));
          
          if (this.activityToEdit) {
            this.populateActivityData();
          }
        }
      },
      error: (error: any) => {
        this.loading = false;
        console.error('Error loading contacts:', error.message);
      }
    });
  }

  populateActivityData(): void {
    this.activityData = {
      activity_name: this.activityToEdit?.activity_name || '',
      location: this.activityToEdit?.location || '',
      primary_contact_id: this.activityToEdit?.primary_contact_id || 0,
      secondary_contact_id: this.activityToEdit?.secondary_contact_id || 0,
      emergency_equipment: this.activityToEdit?.emergency_equipment || '',
      first_aid_contact_ids: [...(this.activityToEdit?.first_aid_contact_ids || [])],
      cpr_contact_ids: [...(this.activityToEdit?.cpr_contact_ids || [])],
      aed_contact_ids: [...(this.activityToEdit?.aed_contact_ids || [])],
      incident_reporting_contact_ids: [...(this.activityToEdit?.incident_reporting_contact_ids || [])],
      ieap_contact_ids: [...(this.activityToEdit?.ieap_contact_ids || [])],
      emergency_diabetic_contact_ids: [...(this.activityToEdit?.emergency_diabetic_contact_ids || [])],
      courses_of_action: this.activityToEdit?.courses_of_action || ''
    };

    // Helper function to map contact IDs → objects for display
    const mapContacts = (ids?: number[]) => {
      return (ids || [])
        .map(id => this.fullContactsList.find(c => c.id === id))
        .filter((c): c is IContact => !!c)
        .map(c => ({ id: c.id, name: c.name, email: c.email }));
    };

    // Populate selectedContacts based on current IDs
    this.selectedContacts.first_aid = mapContacts(this.activityData.first_aid_contact_ids);
    this.selectedContacts.cpr = mapContacts(this.activityData.cpr_contact_ids);
    this.selectedContacts.aed = mapContacts(this.activityData.aed_contact_ids);
    this.selectedContacts.incident_reporting = mapContacts(this.activityData.incident_reporting_contact_ids);
    this.selectedContacts.ieap = mapContacts(this.activityData.ieap_contact_ids);
    this.selectedContacts.emergency_diabetic = mapContacts(this.activityData.emergency_diabetic_contact_ids);
  }

  addContact(field: keyof typeof this.selectedContacts, contactId: string): void {
    const contact = this.contactsList.find(c => c.id === Number(contactId));
    if (contact && !this.selectedContacts[field].some(c => c.id === contact.id)) {
      this.selectedContacts[field].push({ id: contact.id, name: contact.name, email: contact.email });
      (this.activityData[`${field}_contact_ids`] as number[]).push(contact.id);
    }
  }

  removeContact(field: keyof typeof this.selectedContacts, index: number): void {
    const removed = this.selectedContacts[field].splice(index, 1)[0];
    if (removed) {
      this.activityData[`${field}_contact_ids`] = (this.activityData[`${field}_contact_ids`] as number[]).filter(
        id => id !== removed.id
      );
    }
  }

  get filteredSuggestions(): string[] {
    const query = this.activityData.emergency_equipment?.toLowerCase() || '';
    return this.suggestions.filter(s => s.toLowerCase().includes(query));
  }

  selectSuggestion(value: string): void {
    this.activityData.emergency_equipment = value;
    this.showSuggestions = false;
  }

  hideSuggestions(): void {
    setTimeout(() => this.showSuggestions = false, 150);
  }

  closeModal(): void {
    this.onClose.emit();
  }

  saveEdit(): void {
    if (this.isValidActivity() && this.activityToEdit?.id && this.currentBuildingId) {
      this.loading = true;
      console.log("Submitting edited material:", this.activityData);

      this.afterschoolActivityService.updateAfterschoolActivity(this.currentBuildingId, String(this.activityToEdit.id), this.activityData).subscribe({
        next: (res) => {
          this.loading = false;
          if (!res.has_error) {
            this.onSave.emit({ type: 'success', message: 'This occurred after updating an after school activity.'});
            this.closeModal();
          }
        },
        error: (err) => {
          this.loading = false;
          this.onSave.emit({ type: 'unsuccess', message: err.message });
        }
      });
    }
  }

  isValidActivity(): boolean {
    if (!this.activityData.activity_name || !this.activityData.courses_of_action) {
      return false;
    }
    // Location and equipment are only collected on the full (AL) form.
    if (this.showFullDetails) {
      return Boolean(this.activityData.location && this.activityData.emergency_equipment);
    }
    return true;
  }

}
